import React, { useEffect, useState, useRef, MutableRefObject } from 'react';
import {
  useServicesContext,
  useButtons,
  EnvConfig,
  CompleteVehicleEntry,
  DynamicFinancingValues,
} from '@oneaudi/vtp-shared';
import { NavigationWrapper } from './Navigation/NavigationWrapper';
import { InformationWrapper } from './InformationWrapper';
import {
  ConversionBarContainer,
  ConversionBarDefaultBar,
  ConversionBarWrapper,
} from '../styles/ConversionBar.styles';
import ReactDOM from 'react-dom';

export interface ConversionBarProps {
  vehicleRaw: CompleteVehicleEntry;
  dynamicFinancingValues?: DynamicFinancingValues;
  updateDynamicFinancingValues?: (values?: DynamicFinancingValues) => void;
}

export const ConversionBar: React.FC<ConversionBarProps> = ({
  vehicleRaw,
  dynamicFinancingValues,
  updateDynamicFinancingValues,
}) => {
  const { getEnvironmentConfig } = useServicesContext();

  const environmentConfig: EnvConfig | null = getEnvironmentConfig();

  const [popOverStatus, setPopoverStatus] = useState(false);

  const stickyBar = useRef<HTMLDivElement | null>(null);

  let elementsFA = null;
  let pageMain = null;
  if (typeof window !== 'undefined') {
    // Safe to use document.querySelectorAll()
    elementsFA = document.querySelectorAll('feature-app');
    pageMain = document.querySelector('main');
  }

  // Get all feature-app with baseurl
  const footerFA = elementsFA
    ? Array.from(elementsFA).find((el) => {
        return el && el.getAttribute('base-url')?.includes('footer');
      })
    : null;

  useEffect(() => {
    const handleScroll = async () => {
      const footerRect = footerFA ? footerFA?.getBoundingClientRect() : null;

      if (stickyBar.current && footerRect) {
        if (footerRect.top < window.innerHeight) {
          // If footer is visible, move the sticky bar upwards based on scroll
          const overlap = window.innerHeight - footerRect.top;

          stickyBar.current.style.transform = `translateY(-${overlap}px)`;
        } else {
          // TODO: fix the sticky bar position when the footer is not visible. The footer ID is not available.
          // Reset the sticky bar to its original position.
          stickyBar.current.style.transform = 'translateY(0)';
        }
      }
    };

    // Add the scroll event listener
    window.addEventListener('scroll', handleScroll);
    handleScroll();

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [popOverStatus]);

  const setPopover = (status: boolean) => setPopoverStatus(status);

  const appContext = 'pdp';

  const { buttonsState, financeButtonsState, scContextId, isDesktop } = useButtons(
    vehicleRaw.basic,
    appContext,
    undefined,
    environmentConfig,
    false,
    false
  );

  const modifiedState = vehicleRaw?.basic?.financing ? financeButtonsState : buttonsState;

  const isColumn = buttonsState && buttonsState.length <= 2;

  const conversionBar = (
    <ConversionBarWrapper data-testid="conversion-bar" ref={stickyBar}>
      <ConversionBarContainer>
        <ConversionBarDefaultBar>
          <NavigationWrapper vehicle={vehicleRaw.basic} isColumn={isColumn} />
          <InformationWrapper
            popOverStatus={popOverStatus}
            vehicleRaw={vehicleRaw}
            setPopover={setPopover}
            scContextId={scContextId}
            buttonsState={modifiedState}
            isColumn={isColumn}
            isDesktop={isDesktop}
            conversionBarRef={stickyBar as MutableRefObject<null>}
            dynamicFinancingValues={dynamicFinancingValues}
            updateDynamicFinancingValues={updateDynamicFinancingValues}
          />
        </ConversionBarDefaultBar>
      </ConversionBarContainer>
    </ConversionBarWrapper>
  );

  return pageMain ? ReactDOM.createPortal(conversionBar, pageMain) : conversionBar;
};
